import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as mapboxgl from 'mapbox-gl';
import { CarIcon } from './icons';
import {
    MotionPoint,
    buildDistanceTable,
    headingDeg,
    nearestEquivalentDeg,
    quantizeDeg,
    sampleByDistance,
} from './deliveryMonitorMotion';

const SOURCE_ID = 'dm-replay';
const LINE_ID = 'dm-replay-line';
const GLOW_ID = 'dm-replay-glow';

// 与 PRD playRoute 一致：匀速 5s 跑完 → 停 5s → 0.5s 淡出
const RUN_MS = 5_000;
const HOLD_MS = 5_000;
const FADE_MS = 500;

interface ReplayState {
    driverAccountId: string;
    raf: number;
    timers: number[];
    marker: mapboxgl.Marker;
}

export interface RouteReplay {
    /** 在主地图上回放一条轨迹；会先停掉已有回放 */
    start: (driverAccountId: string, color: string, points: MotionPoint[]) => void;
    /** 立即中止并清理图层，恢复实时小车；没有回放时为 no-op */
    stop: () => void;
    /** 正在回放的司机，没有为 null */
    activeDriverId: () => string | null;
}

/**
 * 当日轨迹回放。轨迹线一次性写入 source，按进度改 `line-trim-offset` 露出已走过的部分，
 * 避免每帧 setData 整条线；回放车用独立 marker，实时小车由调用方按 setLiveCarHidden 隐藏/恢复。
 */
export const useRouteReplay = (
    mapRef: RefObject<mapboxgl.Map | null>,
    setLiveCarHidden: (driverAccountId: string, hidden: boolean) => void,
): { replay: RouteReplay; replayCarNode: React.ReactNode } => {
    const stateRef = useRef<ReplayState | null>(null);
    const [car, setCar] = useState<{ host: HTMLDivElement; color: string } | null>(null);
    const hiddenCbRef = useRef(setLiveCarHidden);
    hiddenCbRef.current = setLiveCarHidden;

    const stop = useCallback(() => {
        const state = stateRef.current;
        if (!state) return;
        stateRef.current = null;
        cancelAnimationFrame(state.raf);
        state.timers.forEach((id) => window.clearTimeout(id));
        state.marker.remove();
        const map = mapRef.current;
        if (map) {
            [LINE_ID, GLOW_ID].forEach((id) => {
                if (map.getLayer(id)) map.removeLayer(id);
            });
            if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
        }
        setCar(null);
        hiddenCbRef.current(state.driverAccountId, false);
    }, [mapRef]);

    const start = useCallback(
        (driverAccountId: string, color: string, points: MotionPoint[]) => {
            stop();
            const map = mapRef.current;
            if (!map || points.length === 0) return;

            const coords = points.map((p) => [p.lng, p.lat] as [number, number]);
            // LineString 至少要 2 个点；只有 1 个点时原地复制一份
            const lineCoords = coords.length === 1 ? [coords[0], coords[0]] : coords;
            const bounds = new mapboxgl.LngLatBounds(coords[0], coords[0]);
            coords.forEach((c) => bounds.extend(c));
            map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 700 });

            map.addSource(SOURCE_ID, {
                type: 'geojson',
                lineMetrics: true,
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: { type: 'LineString', coordinates: lineCoords },
                },
            });
            const layout = { 'line-cap': 'round' as const, 'line-join': 'round' as const };
            map.addLayer({
                id: GLOW_ID,
                type: 'line',
                source: SOURCE_ID,
                layout,
                paint: { 'line-color': color, 'line-width': 11, 'line-opacity': 0.16, 'line-trim-offset': [0, 1] },
            });
            map.addLayer({
                id: LINE_ID,
                type: 'line',
                source: SOURCE_ID,
                layout,
                paint: { 'line-color': color, 'line-width': 3.4, 'line-opacity': 0.95, 'line-trim-offset': [0, 1] },
            });

            const element = document.createElement('div');
            element.style.cssText = `width:34px;height:46px;z-index:5;pointer-events:none;transition:opacity ${FADE_MS}ms;`;
            const rotor = document.createElement('div');
            rotor.style.cssText = 'width:34px;height:46px;';
            element.appendChild(rotor);
            const marker = new mapboxgl.Marker({ element, anchor: 'center' }).setLngLat(coords[0]).addTo(map);

            const state: ReplayState = { driverAccountId, raf: 0, timers: [], marker };
            stateRef.current = state;
            setCar({ host: rotor, color });
            hiddenCbRef.current(driverAccountId, true);

            const table = buildDistanceTable(points);
            const t0 = performance.now();
            let rotation: number | null = null;
            const step = (now: number) => {
                if (stateRef.current !== state) return;
                const p = Math.min(1, (now - t0) / RUN_MS);
                const s = sampleByDistance(points, table, p);
                marker.setLngLat([s.lng, s.lat]);
                // [p, 1] 区间被裁掉，露出的就是已走过的部分
                map.setPaintProperty(LINE_ID, 'line-trim-offset', [p, 1]);
                map.setPaintProperty(GLOW_ID, 'line-trim-offset', [p, 1]);
                const next = points[s.segment + 1];
                const heading = next ? headingDeg(points[s.segment], next) : null;
                if (heading !== null) {
                    const deg = nearestEquivalentDeg(rotation, quantizeDeg(heading, 5));
                    if (deg !== rotation) {
                        rotor.style.transform = `rotate(${deg}deg)`;
                        rotation = deg;
                    }
                }
                if (p < 1) {
                    state.raf = requestAnimationFrame(step);
                    return;
                }
                state.timers.push(
                    window.setTimeout(() => {
                        [LINE_ID, GLOW_ID].forEach((id) => {
                            map.setPaintProperty(id, 'line-opacity-transition', { duration: FADE_MS, delay: 0 });
                            map.setPaintProperty(id, 'line-opacity', 0);
                        });
                        element.style.opacity = '0';
                        state.timers.push(window.setTimeout(stop, FADE_MS + 20));
                    }, HOLD_MS),
                );
            };
            state.raf = requestAnimationFrame(step);
        },
        [mapRef, stop],
    );

    // 卸载兜底；地图销毁前调用方需先 stop（map.remove 之后不能再操作图层）
    useEffect(() => stop, [stop]);

    const replay = useMemo<RouteReplay>(
        () => ({
            start,
            stop,
            activeDriverId: () => stateRef.current?.driverAccountId ?? null,
        }),
        [start, stop],
    );

    const replayCarNode = car ? createPortal(<CarIcon color={car.color} selected />, car.host) : null;

    return { replay, replayCarNode };
};
